// 文件用途：消息中心 API 与消息数据归一化逻辑。
/**
 * @file 消息中心 API
 */
// 引入统一请求方法与请求枚举。
import request, { MethodEnum, serverTriggerUrl } from '@/utils/fetch';
// 引入 HTML 实体解码工具。
import { decodeHtmlEntities } from '@/utils/html';

// 定义消息类型枚举。
export enum MESSAGE_TYPE {
  // 系统消息类型。
  SYSTEM = 'system',
  // 点赞消息类型。
  LIKE = 'like',
  // 回复消息类型。
  REPLY = 'reply',
}

// CMS 用户为空错误码常量。
export const CMS_USER_ID_EMPTY = 10302;
// CMS 请求参数为空错误码常量。
export const CMS_REQUEST_PARAM_EMPTY = 10201;

// 定义通知项数据结构。
export interface INotificationItem {
  // 通知主键 ID。
  id: number;
  // 接收消息用户 ID。
  uid: number;
  // 通知类型。
  type: string;
  // 通知大类标题。
  parent_type: string;
  // 是否新消息标识。
  is_new: number;
  // 聚合条数。
  num: number;
  // 触发者用户 ID。
  authorid: number;
  // 触发者用户名。
  author: string;
  // 通知正文。
  note: string;
  // 时间戳。
  dateline: number;
  // 来源对象 ID。
  from_id: number;
  // 来源对象类型。
  from_idtype: string;
  // 分类 ID。
  category: number;
  // 关联帖子 ID。
  tid: number;
  // 评论偏移量。
  offset: number;
}

// 定义通知列表响应结构。
export interface IGetNotificationListRes {
  // 通知项数组。
  items: INotificationItem[];
  // 总条数。
  total_items: number;
}

// 定义旧版消息列表项结构。
export interface IMessageItem {
  // 消息 ID。
  id: number;
  // 消息类型。
  type: MESSAGE_TYPE | string;
  // 标题。
  title: string;
  // 内容。
  content: string;
  // 时间文本。
  time: string;
  // 是否已读。
  isRead: boolean;
  /** 跳转链接或关联ID */
  // 跳转链接。
  link?: string;
  // 目标 ID。
  targetId?: string | number;
  // 来源对象 ID，例如评论 pid。
  fromId?: string | number;
  /** 是否显示右侧箭头 */
  // 是否展示右箭头。
  hasArrow: boolean;
  /** 是否为系统通知 */
  // 是否系统消息。
  isSystem?: boolean;
}

// 定义旧版消息列表响应结构。
export interface IGetMessageListRes {
  // 列表项。
  items: IMessageItem[];
  // 总数。
  total: number;
  // 是否还有更多。
  hasMore: boolean;
}

// 定义未读消息数响应结构。
export interface IUserMessagesRes {
  // 未读消息数。
  messages: number;
}

// 去除 HTML 标签，保留纯文本。
const stripHtmlTags = (text = '') => text.replace(/<[^>]+>/g, '');

// 归一化空白字符，避免多空格换行影响展示。
const normalizeWhitespace = (text = '') => text.replace(/\s+/g, ' ').trim();

// 转换用户组变更系统通知文案。
const transformChangeUserGroupNote = (note = '') => {
  // 匹配前缀与第一个锚点文本。
  const firstAnchorMatch = note.match(/^(.*?)<a[^>]*>(.*?)<\/a>/i);
  // 命中锚点时分别处理前缀与锚点文本。
  if (firstAnchorMatch) {
    // 解码并规整前缀文本。
    const prefix = normalizeWhitespace(
      // 先去标签再解码实体。
      decodeHtmlEntities(stripHtmlTags(firstAnchorMatch[1]), {
        // 补充 rsaquo 自定义实体映射。
        namedEntityMap: { rsaquo: '›' },
      })
    );
    // 解码并规整用户组名称。
    const groupName = normalizeWhitespace(
      // 先去标签再解码实体。
      decodeHtmlEntities(stripHtmlTags(firstAnchorMatch[2]), {
        // 补充 rsaquo 自定义实体映射。
        namedEntityMap: { rsaquo: '›' },
      })
    );
    // 拼接最终文案返回。
    return normalizeWhitespace(`${prefix} ${groupName}`);
  }

  // 未命中锚点时按普通文本处理。
  return normalizeWhitespace(
    // 去标签并解码实体。
    decodeHtmlEntities(stripHtmlTags(note), {
      // 补充 rsaquo 自定义实体映射。
      namedEntityMap: { rsaquo: '›' },
    })
  );
};

// 归一化单条通知项内容。
const normalizeNotificationItem = (item: INotificationItem): INotificationItem => {
  // 仅对系统通知且来源为用户组变更场景做特殊处理。
  if (item.parent_type === '系统通知' && item.from_idtype === 'changeusergroup') {
    // 返回处理后的通知项。
    return {
      // 保留原字段。
      ...item,
      // 覆盖 note 字段为转换后的文本。
      note: transformChangeUserGroupNote(item.note || ''),
    };
  }
  // 其他类型直接返回原始数据。
  return item;
};

// 归一化通知列表载荷。
const normalizeNotificationPayload = (payload: IGetNotificationListRes): IGetNotificationListRes => {
  // 非数组数据直接透传。
  if (!Array.isArray(payload?.items)) {
    // 返回原 payload。
    return payload;
  }
  // 对每一项执行归一化处理。
  return {
    // 保留原字段。
    ...payload,
    // 映射处理 items。
    items: payload.items.map(normalizeNotificationItem),
  };
};

// 获取通知列表接口。
/**
 * 获取消息列表
 * @param params
 */
export const getNotificationList = (
  // 分页入参。
  params: { offset: number; limit: number }
): Promise<IGetNotificationListRes> => {
  // 发送请求并兼容多种历史响应格式。
  return request({
    // 接口路径。
    url: '/user/notifications',
    // 请求方法。
    method: MethodEnum.POST,
    // 请求参数。
    params: params,
  }).then((res: any) => {
    // 标准 code=0 格式。
    if (res.code === 0) {
      // 返回 data 并做归一化。
      return normalizeNotificationPayload(res.data);
    }
    // 兼容直接返回 items 的格式。
    if (res.items) {
      // 对结果做归一化。
      return normalizeNotificationPayload(res);
    }
    // 兼容 data.items 格式。
    if (res.data && res.data.items) {
      // 对结果做归一化。
      return normalizeNotificationPayload(res.data);
    }
    // 无法识别格式时抛出异常。
    return Promise.reject(res);
  });
};

// 获取未读消息数接口。
export const getUserMessages = (): Promise<IUserMessagesRes> => {
  // 请求未读消息数并兼容多个字段口径。
  return request({
    // 接口路径。
    url: '/users/messages',
    // 请求方法。
    method: MethodEnum.GET,
  }).then((res: any) => {
    // 兼容 data 包裹或直接对象。
    const payload = res?.data ?? res;
    // 兼容 item/messages/total/count/unread 等字段。
    const messages = Number(
      payload?.item ?? payload?.messages ?? payload?.total ?? payload?.count ?? payload?.unread ?? payload
    );
    // 数值有效时返回标准结构。
    if (Number.isFinite(messages)) {
      // 返回标准消息数对象。
      return { messages };
    }
    // 无法解析时抛错。
    return Promise.reject(res);
  });
};

// 旧版接口兼容占位实现。
export const getMessageList = (): Promise<IGetMessageListRes> => {
  // 当前暂未接入旧版列表，返回空数据结构。
  return Promise.resolve({
    // 空列表。
    items: [],
    // 总数 0。
    total: 0,
    // 无更多。
    hasMore: false
  });
};
